import { useTranslation } from 'react-i18next';
import { currentLocale } from '@shared/i18n';

interface TopPost {
  id: string; permalink: string | null; thumb: string | null;
  views: number | null; likes: number | null; posted_at: string | null; type: string | null;
}

const fmtK = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1_000_000 ? `${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n);

/** "every Nth post" reads instantly; a percentage does not. */
const everyNth = (pct: number): number => Math.max(2, Math.round(100 / Math.max(pct, 1)));

/**
 * Two things a creator can't see about themselves but every buyer can:
 * how ad-heavy their feed is versus similar creators, and what a mention is
 * actually worth to a brand. Both are framed as something to act on, not as
 * statistics — the percentage alone means nothing without the peer number.
 */
export function CreatorAdLoad({ adLoad, peers, storyAdLoad, postsCount }: {
  adLoad: number | null; peers: number | null; storyAdLoad: number | null; postsCount: number | null;
}) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o }) as string;
  if (adLoad == null || peers == null) return null;

  // Single block: the number + peer comparison always; the "cut it down" advice
  // (and orange framing) only when the feed is notably ad-heavier than peers.
  const heavy = adLoad > peers * 1.4;
  const mine = everyNth(adLoad);
  const target = everyNth(peers);
  const accent = heavy ? '#e8965a' : '#8fb0e6';

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 12, color: 'var(--dim)', margin: '0 2px 8px' }}>{tr('creator.adload.title', 'Рекламы в постах')}</div>
      <div style={{ background: heavy ? 'rgba(232,150,90,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${heavy ? 'rgba(232,150,90,0.35)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 12, padding: '13px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 26, fontWeight: 700, color: accent, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{adLoad}%</span>
          <span style={{ fontSize: 13, color: 'var(--dim)' }}>{tr('creator.adload.peers', 'у похожих {{p}}%', { p: peers })}</span>
        </div>
        <div style={{ position: 'relative', height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', marginTop: 11 }}>
          <div style={{ width: `${Math.min(adLoad, 100)}%`, height: '100%', borderRadius: 3, background: accent }} />
          <div title={tr('creator.adload.peerMark', 'у похожих')}
            style={{ position: 'absolute', top: -3, left: `${Math.min(peers, 100)}%`, width: 2, height: 12, background: '#9aa3b5' }} />
        </div>
        {heavy && (
          <>
            <div style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.45, marginTop: 11 }}>
              {tr('creator.adload.advice', 'Снизьте долю рекламы: у вас каждый {{mine}}-й пост рекламный, у похожих — каждый {{target}}-й.', { mine, target })}
            </div>
            <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 6, lineHeight: 1.5 }}>
              {tr('creator.adload.why', 'Чем плотнее реклама, тем хуже реагирует аудитория — и бренды смотрят на это, прежде чем позвать.')}
            </div>
          </>
        )}
        {storyAdLoad != null && (
          <div style={{ fontSize: 11.5, color: '#7a8194', marginTop: 8 }}>
            {tr('creator.adload.storiesLine', 'В сторис — {{p}}% рекламы', { p: storyAdLoad })}
            {postsCount ? ` · ${tr('creator.adload.basisPosts', 'посты: {{n}} за 3 месяца', { n: postsCount })}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}

/** Their strongest posts — the format averages never answer "what should I make next". */
export function CreatorTopPosts({ posts }: { posts: TopPost[] }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d }) as string;
  if (!posts?.length) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 12, color: 'var(--dim)', margin: '0 2px 8px' }}>
        {tr('creator.top.title', 'Ваши лучшие посты')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {posts.map((p) => (
          <a key={p.id} href={p.permalink ?? '#'} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
            <div style={{ position: 'relative', aspectRatio: '3 / 4', borderRadius: 10, overflow: 'hidden', background: '#161a24', border: '1px solid rgba(255,255,255,0.08)' }}>
              {p.thumb && <img src={p.thumb} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '16px 6px 5px', background: 'linear-gradient(transparent, rgba(0,0,0,0.82))', color: '#fff', fontSize: 11.5, fontWeight: 600 }}>
                {fmtK(p.views)}
              </div>
            </div>
            {p.posted_at && (
              <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 4 }}>
                {new Date(p.posted_at).toLocaleDateString(currentLocale(), { day: '2-digit', month: 'short' })}
              </div>
            )}
          </a>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 7 }}>
        {tr('creator.top.note', 'По просмотрам за 3 месяца. Нажмите, чтобы открыть в Instagram.')}
      </div>
    </div>
  );
}
